import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import Input from '../../../reusable/Input/Input';
import NumberInput from '../../../reusable/NumberInput/NumberInput';
import Select from '../../../reusable/Select/Select';
import AuthSlider from '../../../reusable/AuthSlider/AuthSlider';
import Button from '../../../reusable/Button/Button';
import { useSignupMutation } from '../../../store/api/authApi';
import { authErrorMessage, SIGNUP_FAILED_MESSAGE } from '../../../store/api/authHelpers';
import {
  BANNED_COUNTRY_CODES,
  countryName,
  DEFAULT_COUNTRY_CODE,
  DEFAULT_DIAL_CODE,
  findCountry,
  SERVED_COUNTRIES,
} from '../../../utilities/country';
import { googleAuthUrl } from '../../../utilities/googleAuth';
import { t } from '../../../i18n/i18n';
import './SignUp.scss';

const MIN_AGE = 18;
const MIN_PASSWORD_LENGTH = 8;

const PASSWORD_RULES = [
  [/[a-z]/, 'a lowercase letter'],
  [/[A-Z]/, 'an uppercase letter'],
  [/\d/, 'a number'],
  [/[^A-Za-z0-9]/, 'a symbol'],
];

const andList = (items) =>
  items.length < 2
    ? items.join('')
    : t('{{items}} and {{last}}', { items: items.slice(0, -1).join(', '), last: items.at(-1) });

const todayIso = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const latestDob = () => dayjs().subtract(MIN_AGE, 'year');

const ageOn = (iso) => {
  const [year, month, day] = String(iso).split('-').map(Number);
  const [thisYear, thisMonth, thisDay] = todayIso().split('-').map(Number);
  const beforeBirthday = thisMonth < month || (thisMonth === month && thisDay < day);
  return thisYear - year - (beforeBirthday ? 1 : 0);
};

const signUpSchema = Yup.object({
  first_name: Yup.string().trim().required('First name is required'),
  last_name: Yup.string().trim().required('Last name is required'),
  date_of_birth: Yup.string()
    .required('Date of birth is required')
    .test('is-iso-date', 'Please enter a valid date', (v) => !v || /^\d{4}-\d{2}-\d{2}$/.test(v))
    .test('is-past', 'Date of birth cannot be in the future', (v) => !v || v <= todayIso())
    .test('min-age', () => t('You must be at least {{age}} to open an account', { age: MIN_AGE }), (v) =>
      !v || v > todayIso() || ageOn(v) >= MIN_AGE),
  country_code: Yup.string().required('Select a dialling code'),
  phone: Yup.string()
    .required('Phone number is required')
    .test(
      'digit-count',
      'Enter a phone number between 6 and 15 digits',
      (v) => {
        if (!v) return true;
        const digits = v.replace(/\D/g, '');
        return digits.length >= 6 && digits.length <= 15;
      },
    ),
  email: Yup.string()
    .trim()
    .email('Please enter a valid email address')
    .required('Email is required'),
  confirmEmail: Yup.string()
    .required('Please confirm your email address')
    .oneOf([Yup.ref('email')], 'Email addresses must match'),
  country: Yup.string()
    .required('Country is required')
    // The list leaves these out already; this catches a value that arrives
    // some other way (autofill, an old draft).
    .test('served', "We don't currently offer accounts in this country", (v) =>
      !BANNED_COUNTRY_CODES.has(v)),
  City: Yup.string().trim().required('City is required'),
  password: Yup.string()
    .required('Password is required')
    .min(MIN_PASSWORD_LENGTH, ({ min }) => t('Use at least {{count}} characters', { count: min }))
    .test('has-variety', 'Password is too simple', function (v) {
      if (!v) return true;
      const missing = PASSWORD_RULES.filter(([re]) => !re.test(v)).map(([, label]) => t(label));
      if (!missing.length) return true;
      return this.createError({ message: t('Add {{items}}', { items: andList(missing) }) });
    })
    .test('no-outer-space', 'Password cannot start or end with a space', (v) => !v || v === v.trim())
    .test('not-personal', 'Do not use your name or email in your password', function (v) {
      if (!v) return true;
      const { first_name: first, last_name: last, email } = this.parent;
      const personal = [first, last, String(email ?? '').split('@')[0]]
        .map((part) => String(part ?? '').trim().toLowerCase())
        .filter((part) => part.length >= 3);
      const lowered = v.toLowerCase();
      return !personal.some((part) => lowered.includes(part));
    }),
});

export default function SignUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [signup, { isLoading, error: serverError }] = useSignupMutation();
  const [failed, setFailed] = useState(null);

  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      date_of_birth: '',
      phone: '',
      country_code: DEFAULT_DIAL_CODE,
      email: '',
      confirmEmail: '',
      country: DEFAULT_COUNTRY_CODE,
      City: '',
      password: '',
    },
    validationSchema: signUpSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setFailed(null);

      const body = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        body.set(key, key === 'password' ? value : String(value).trim());
      });
      body.set('full_name', `${values.first_name} ${values.last_name}`.trim());
      body.set('email', values.email.trim().toLowerCase());
      body.set('country', countryName(values.country));
      body.set('country_code', values.country_code.replace(/\D/g, ''));
      body.set('referal_code', '');
      body.set('platform', 'web');

      try {
        const session = await signup(body).unwrap();

        if (!session?.firebase_cred?.idToken) {
          setFailed(SIGNUP_FAILED_MESSAGE);
          return;
        }

        navigate('/dashboard', { replace: true });
      } catch {
        // Swallowed: surfaced through `serverError` below.
      } finally {
        setSubmitting(false);
      }
    },
  });

  const fieldError = (name) => (formik.touched[name] ? t(formik.errors[name]) : undefined);

  const touch = (name) => () => formik.setFieldTouched(name, true);

  const handleEmailChange = (e) =>
    formik.setFieldValue(e.target.name, e.target.value.toLowerCase());

  const signUpWithGoogle = () => {
    window.location.href = googleAuthUrl(new URLSearchParams(location.search).get('next'));
  };

  return (
    <div className="signup-page-split">
      <div className="signup-banner-side">
        <AuthSlider />
      </div>

      <div className="signup-form-side">
        <div className="signup-brand-logo">
          <img src="/images/Logo.png" alt={t('Alpha Capital Logo')} className="logo-img" />
        </div>
        <div className="signup-form-content">
          <div className="signup-form-header">
            <h1 className="signup-title">{t('Sign Up')}</h1>
            <p className="signup-description">{t('Create your Alpha Capital account')}</p>
          </div>

          <form onSubmit={formik.handleSubmit} className="signup-form-body" noValidate>
            {(failed || serverError) && (
              <div className="signup-form-error" role="alert">
                {t(failed ?? authErrorMessage(serverError))}
              </div>
            )}

            <div className="signup-row">
              <div className="signup-col">
                <Input
                  label={t('First Name')}
                  name="first_name"
                  placeholder="John"
                  value={formik.values.first_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={fieldError('first_name')}
                  disabled={isLoading}
                  autoComplete="given-name"
                />
              </div>
              <div className="signup-col">
                <Input
                  label={t('Last Name')}
                  name="last_name"
                  placeholder="Smith"
                  value={formik.values.last_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={fieldError('last_name')}
                  disabled={isLoading}
                  autoComplete="family-name"
                />
              </div>
            </div>

            <Input
              label={t('Date of Birth')}
              type="date"
              name="date_of_birth"
              placeholder={t('Date of birth')}
              value={formik.values.date_of_birth}
              onChange={formik.handleChange}
              onBlur={touch('date_of_birth')}
              error={fieldError('date_of_birth')}
              disabled={isLoading}
              disabledDate={(current) => current && current.isAfter(latestDob(), 'day')}
              // A future or under-age date typed by hand is refused, with why.
              rejectMessage={(date) =>
                date.isAfter(dayjs(), 'day')
                  ? t('Date of birth cannot be in the future')
                  : t('You must be at least {{age}} to open an account', { age: MIN_AGE })
              }
              defaultPickerValue={latestDob()}
              startPanel="year"
            />

            <Select
              label={t('Country')}
              name="country"
              value={formik.values.country}
              countries={SERVED_COUNTRIES}
              // Picking a country sets the phone's dialling code to match, as
              // the live dashboard does (AC1-I139). The prefix can still be
              // changed by hand afterwards.
              onChange={(event) => {
                formik.handleChange(event);
                const dialCode = findCountry(event.target.value)?.dialCode;
                if (dialCode) formik.setFieldValue('country_code', dialCode);
              }}
              onBlur={touch('country')}
              error={fieldError('country')}
              disabled={isLoading}
            />

            <NumberInput
              label={t('Number')}
              name="phone"
              placeholder={t('Phone Number')}
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              countryCode={formik.values.country_code}
              countryIso={formik.values.country}
              onCountryCodeChange={(code) => formik.setFieldValue('country_code', code)}
              // Banned countries are left out here too, not only in Country.
              countries={SERVED_COUNTRIES}
              error={fieldError('phone') ?? fieldError('country_code')}
              disabled={isLoading}
              autoComplete="tel-national"
            />

            <Input
              label={t('Email')}
              type="email"
              name="email"
              placeholder="john@example.com"
              value={formik.values.email}
              onChange={handleEmailChange}
              onBlur={formik.handleBlur}
              error={fieldError('email')}
              disabled={isLoading}
              autoComplete="email"
            />

            <Input
              label={t('Confirm Email Address')}
              type="email"
              name="confirmEmail"
              placeholder="john@example.com"
              value={formik.values.confirmEmail}
              onChange={handleEmailChange}
              onBlur={formik.handleBlur}
              error={fieldError('confirmEmail')}
              disabled={isLoading}
              autoComplete="email"
            />

            <Input
              label={t('City')}
              name="City"
              placeholder={t('City')}
              value={formik.values.City}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={fieldError('City')}
              disabled={isLoading}
              autoComplete="address-level2"
            />

            <Input
              label={t('Password')}
              type="password"
              name="password"
              placeholder={t('Enter your password')}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={fieldError('password')}
              disabled={isLoading}
              autoComplete="new-password"
            />

            <Button variant="auth" htmlType="submit" loading={isLoading} disabled={isLoading}>
              {isLoading ? t('Creating account…') : t('Sign Up')}
            </Button>

            <div className="signup-bottom-section">
              <div className="signup-divider">
                <div className="signup-divider-line" />
                <span className="signup-divider-text">{t('or')}</span>
                <div className="signup-divider-line" />
              </div>

              <button
                type="button"
                className="signup-google-button"
                onClick={signUpWithGoogle}
                disabled={isLoading}
              >
                <img src="/icons/Google.svg" alt="Google" style={{ width: '19px', height: '19px' }} />
                <span className="button-text">{t('Sign up with Google')}</span>
              </button>

              <div className="signup-footer-text">
                {t('Already have an account?')}{" "}
                <Link to="/sign-in" className="signup-signin-link">
                  {t('Sign In')}
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
